import {
  AfterViewInit,
  Component,
  EventEmitter,
  OnDestroy,
  Output,
} from '@angular/core';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/plugins/record';
import { PERMISSION_STATES, PermissionError } from '@constants';

@Component({
  selector: 'unicon-audio-recording-field',
  templateUrl: './audio-recording-field.component.html',
  styleUrls: ['./audio-recording-field.component.scss'],
})
export class AudioRecordingFieldComponent implements AfterViewInit, OnDestroy {
  @Output() audioFileRecorded = new EventEmitter<File>();
  @Output() audioPermissionError = new EventEmitter<Error>();
  @Output() audioRecordingStarted = new EventEmitter<void>();

  protected isRecording = true;
  protected isPlayingRecording = false;
  protected formattedRecordingTime = '00:00';

  private permissionState: PermissionState | 'unknown' = 'unknown';
  private wavesurfer: WaveSurfer | null = null;
  private recording: RecordPlugin | null = null;
  private permissionStatus: PermissionStatus | null = null;

  ngAfterViewInit(): void {
    this.checkMicrophonePermission().catch(() => {
      this.cleanup();
    });
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  protected stopRecording(): void {
    this.recording?.stopRecording();
  }

  protected togglePlayPause(): void {
    this.wavesurfer?.playPause().then(() => {
      this.isPlayingRecording = !this.isPlayingRecording;
    });
  }

  private async checkMicrophonePermission(): Promise<void> {
    this.permissionStatus = await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    });
    this.permissionStatus.addEventListener(
      'change',
      this.permissionChangeHandler,
    );

    this.permissionState = this.permissionStatus.state;
    if (this.permissionState === PERMISSION_STATES.GRANTED) {
      this.initializeRecorder();
      return;
    }

    if (this.permissionState === PERMISSION_STATES.DENIED) {
      this.emitPermissionError(PermissionError.MICROPHONE_PERMISSION_DENIED);
      return;
    }

    await this.requestMicrophoneAccess();
  }

  private async requestMicrophoneAccess(): Promise<void> {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(track => track.stop());
  }

  private initializeRecorder(): void {
    if (this.wavesurfer) {
      return;
    }

    this.wavesurfer = WaveSurfer.create({
      container: '#waveform',
      waveColor: 'rgb(123,143,175)',
      progressColor: 'rgb(67,74,85)',
      backend: 'MediaElement',
      height: 20,
      barWidth: 1,
      barHeight: 2,
      barGap: 2,
      barRadius: 2,
      interact: true,
      cursorColor: '#939c9b',
      cursorWidth: 3,
    });

    this.recording = this.wavesurfer.registerPlugin(
      RecordPlugin.create({
        scrollingWaveform: true,
        continuousWaveformDuration: 20,
        renderRecordedAudio: true,
        mimeType: 'audio/webm',
      }),
    );

    this.setupEventListeners();
    this.recording?.startRecording();
  }

  private setupEventListeners(): void {
    if (!this.recording || !this.wavesurfer) {
      return;
    }

    this.recording.on('record-progress', (time: number) =>
      this.updateFormattedTime(time, true),
    );
    this.recording.on('record-start', () => this.audioRecordingStarted.emit());
    this.recording.on('record-end', (blob: Blob) => {
      this.isRecording = false;
      this.audioFileRecorded.emit(
        new File([blob], 'audio.webm', { type: blob.type }),
      );
    });

    this.wavesurfer.on('decode', (duration: number) =>
      this.updateFormattedTime(duration),
    );
    this.wavesurfer.on('timeupdate', (currentTime: number) =>
      this.updateFormattedTime(currentTime),
    );
  }

  private updateFormattedTime(time: number, isProgress = false): void {
    const totalSeconds = isProgress
      ? Math.floor(time / 1000)
      : Math.round(time);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    this.formattedRecordingTime = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  private emitPermissionError(message: string): void {
    this.audioPermissionError.emit(new Error(message));
    this.cleanup();
  }

  private permissionChangeHandler = () => {
    this.permissionState = this.permissionStatus?.state ?? 'unknown';

    if (
      this.permissionState === PERMISSION_STATES.GRANTED &&
      !this.wavesurfer
    ) {
      this.initializeRecorder();
      return;
    }

    if (this.permissionState === PERMISSION_STATES.DENIED && this.isRecording) {
      this.emitPermissionError(
        PermissionError.MICROPHONE_PERMISSION_WAS_DENIED,
      );
    }
  };

  private cleanup(): void {
    if (this.permissionStatus) {
      this.permissionStatus.removeEventListener(
        'change',
        this.permissionChangeHandler,
      );
    }

    if (this.recording && this.isRecording) {
      this.recording.stopRecording();
    }

    if (this.wavesurfer) {
      this.wavesurfer.pause();
      this.wavesurfer.destroy();
    }
  }
}
